/**
 * EP P2P USDT pay.
 *
 * Staging (MockUSDT): settle via `/usdt-mock-pay` — WalletConnect is too flaky on mobile.
 * Prod (real USDT): claim → wallet transfer → markPaid, with chain poll recovery.
 */
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  useAccount,
  useChainId,
  usePublicClient,
  useSwitchChain,
  useWalletClient,
} from 'wagmi';
import { bsc } from 'viem/chains';
import {
  erc20Abi,
  formatUnits,
  getAddress,
  parseAbiItem,
  parseUnits,
  type Address,
  type Hash,
  type PublicClient,
} from 'viem';
import { ApiError } from '../api/client';
import { epAPI, type EpUsdtPayConfig } from '../api/ep';
import { localizeEpApiError } from '../utils/ep-api-error';
import { useWalletAuthLogin } from '../wallet/useWalletAuthLogin';
import { createBscPublicClient } from '../wallet/wait-for-usdt-transfer';

const TARGET_CHAIN_ID = bsc.id;
const TRANSFER_EVENT = parseAbiItem(
  'event Transfer(address indexed from, address indexed to, uint256 value)',
);

const inflightPays = new Map<string, Promise<Hash | undefined>>();
const inflightWrites = new Map<string, Promise<Hash>>();

function normAddr(a: string): string {
  return a.trim().toLowerCase();
}

function errMessage(e: unknown, t?: (key: string) => string): string {
  if (t) return localizeEpApiError(e, t);
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return String(e ?? 'USDT transfer failed');
}

function isUserReject(msg: string): boolean {
  return /user rejected|denied|rejected the request|用户拒绝/i.test(msg);
}

function isWalletInFlight(msg: string): boolean {
  return /duplicate call|already pending|request already pending|transaction already/i.test(msg);
}

async function findMatchingTransfer(opts: {
  publicClient: PublicClient;
  token: Address;
  from: Address;
  to: Address;
  minRaw: bigint;
  fromBlock: bigint;
}): Promise<Hash | null> {
  const latest = await opts.publicClient.getBlockNumber();
  const fromBlock = opts.fromBlock > 0n ? opts.fromBlock : latest > 80n ? latest - 80n : 0n;
  const logs = await opts.publicClient.getLogs({
    address: opts.token,
    event: TRANSFER_EVENT,
    args: { from: opts.from, to: opts.to },
    fromBlock,
    toBlock: 'latest',
  });
  for (let i = logs.length - 1; i >= 0; i--) {
    const log = logs[i]!;
    if (log.args.value != null && log.args.value >= opts.minRaw && log.transactionHash) {
      return log.transactionHash;
    }
  }
  return null;
}

function sleep(ms: number) {
  return new Promise<void>((r) => setTimeout(r, ms));
}

async function waitForTransferOrHash(opts: {
  writePromise: Promise<Hash>;
  publicClient: PublicClient | undefined;
  token: Address;
  from: Address;
  to: Address;
  minRaw: bigint;
  startBlock: bigint;
  timeoutMs?: number;
}): Promise<Hash> {
  const timeoutMs = opts.timeoutMs ?? 120_000;
  const started = Date.now();
  type WriteOutcome = { ok: true; hash: Hash } | { ok: false; err: unknown };
  const writeBox: { current: WriteOutcome | null } = { current: null };
  void opts.writePromise.then(
    (hash) => {
      writeBox.current = { ok: true, hash };
    },
    (err: unknown) => {
      writeBox.current = { ok: false, err };
    },
  );

  while (Date.now() - started < timeoutMs) {
    const settled = writeBox.current;
    if (settled?.ok === true) return settled.hash;
    if (settled?.ok === false) {
      const msg = errMessage(settled.err);
      if (isUserReject(msg)) throw new Error('已取消钱包签名');
      if (!isWalletInFlight(msg) && !opts.publicClient) {
        throw settled.err instanceof Error ? settled.err : new Error(msg);
      }
    }
    if (opts.publicClient) {
      try {
        const found = await findMatchingTransfer({
          publicClient: opts.publicClient,
          token: opts.token,
          from: opts.from,
          to: opts.to,
          minRaw: opts.minRaw,
          fromBlock: opts.startBlock,
        });
        if (found) return found;
      } catch {
        /* keep waiting */
      }
    }
    await sleep(2500);
  }
  throw new Error('等待钱包确认超时，请打开钱包确认后到「我的购买」再点支付');
}

export function useEpUsdtPayment() {
  const { t } = useTranslation();
  const wallet = useWalletAuthLogin();
  const { address, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const publicClient = usePublicClient({ chainId: TARGET_CHAIN_ID });
  const { data: walletClient } = useWalletClient({ chainId: TARGET_CHAIN_ID });
  const { switchChainAsync } = useSwitchChain();

  const [payConfig, setPayConfig] = useState<EpUsdtPayConfig | null>(null);
  const [txHash, setTxHash] = useState<`0x${string}` | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [isPaying, setIsPaying] = useState(false);

  const prepareMut = useMutation({
    mutationFn: (order_id: string) => epAPI.prepareUsdtPay({ order_id }),
    onSuccess: (cfg) => {
      setPayConfig(cfg);
      setError(null);
    },
    onError: (e: unknown) => {
      setError(errMessage(e, t));
    },
  });

  const { data: usdtBalanceRaw } = useQuery({
    queryKey: ['bsc-usdt-balance', payConfig?.usdt_contract, payConfig?.buyer_wallet],
    enabled: Boolean(
      payConfig?.buyer_wallet && payConfig.usdt_contract && !payConfig.mock_pay_allowed,
    ),
    queryFn: () => {
      const token = getAddress(payConfig!.usdt_contract!);
      const owner = getAddress(payConfig!.buyer_wallet!);
      return createBscPublicClient().readContract({
        address: token,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [owner],
      });
    },
    refetchInterval: 15_000,
  });

  const usdtBalance = useMemo(() => {
    if (usdtBalanceRaw == null || payConfig?.usdt_decimals == null) return '0';
    return formatUnits(usdtBalanceRaw, payConfig.usdt_decimals);
  }, [usdtBalanceRaw, payConfig?.usdt_decimals]);

  const isBusy =
    isPaying ||
    prepareMut.isPending ||
    isMarkingPaid ||
    (activeOrderId != null && inflightPays.has(activeOrderId));

  const ensureWalletReady = useCallback(
    async (boundBuyerWallet?: string | null): Promise<boolean> => {
      if (!wallet.isConfigured) {
        setError(t('epTrading.errors.walletNotConfigured'));
        return false;
      }
      if (!isConnected || !address) {
        await wallet.openWalletModal();
        return false;
      }
      if (connectedChainId !== TARGET_CHAIN_ID) {
        try {
          await switchChainAsync({ chainId: TARGET_CHAIN_ID });
        } catch (e) {
          setError(errMessage(e, t) || t('epTrading.errors.switchToBsc'));
          return false;
        }
      }
      if (boundBuyerWallet && normAddr(address) !== normAddr(boundBuyerWallet)) {
        setError(t('epTrading.errors.useBoundWallet'));
        return false;
      }
      return true;
    },
    [wallet, isConnected, address, connectedChainId, switchChainAsync, t],
  );

  const payWithUsdt = useCallback(
    async (orderId: string) => {
      const existing = inflightPays.get(orderId);
      if (existing) {
        setActiveOrderId(orderId);
        setIsPaying(true);
        try {
          return await existing;
        } finally {
          setIsPaying(false);
        }
      }

      let resolvePay!: (h: Hash | undefined) => void;
      let rejectPay!: (e: unknown) => void;
      const gate = new Promise<Hash | undefined>((resolve, reject) => {
        resolvePay = resolve;
        rejectPay = reject;
      });
      inflightPays.set(orderId, gate);

      setIsPaying(true);
      setError(null);
      setTxHash(null);
      setActiveOrderId(orderId);

      try {
        let cfg = payConfig;
        if (!cfg || cfg.order_id !== orderId) {
          cfg = await prepareMut.mutateAsync(orderId);
          setPayConfig(cfg);
        }

        // Always real wallet USDT transfer (no mock settle shortcut).
        if (!cfg.seller_wallet || !cfg.usdt_contract || cfg.usdt_decimals == null) {
          throw new Error(t('epTrading.errors.invalidPayConfig'));
        }
        if (!cfg.buyer_wallet) {
          throw new Error(t('epTrading.errors.buyerNoUsdtWallet'));
        }

        const ready = await ensureWalletReady();
        if (!ready && !isConnected) {
          resolvePay(undefined);
          return undefined;
        }

        const walletReady = await ensureWalletReady(cfg.buyer_wallet);
        if (!walletReady || !address) {
          throw new Error(t('epTrading.errors.connectBoundWallet'));
        }

        const wc = walletClient;
        if (!wc) {
          throw new Error(t('epTrading.errors.walletNotReady'));
        }

        let liveBal = usdtBalance;
        if (publicClient) {
          try {
            const raw = await publicClient.readContract({
              address: cfg.usdt_contract as Address,
              abi: erc20Abi,
              functionName: 'balanceOf',
              args: [cfg.buyer_wallet as Address],
            });
            liveBal = formatUnits(raw, cfg.usdt_decimals);
          } catch {
            /* fall back */
          }
        }

        const payAmount = cfg.payment_usdt;
        const payN = Number(payAmount);
        const balN = Number(liveBal);
        if (Number.isFinite(balN) && Number.isFinite(payN) && payN > balN) {
          throw new Error(
            `${t('epTrading.errors.insufficientUsdt')} (need ${payAmount}, have ${liveBal})`,
          );
        }

        let claimedThisSession = false;
        if (cfg.status !== 'claimed') {
          await epAPI.claim({ order_id: orderId });
          claimedThisSession = true;
        }

        const token = cfg.usdt_contract as Address;
        const seller = cfg.seller_wallet as Address;
        const amountRaw = parseUnits(payAmount, cfg.usdt_decimals);
        const startBlock = publicClient ? await publicClient.getBlockNumber() : 0n;

        let writePromise = inflightWrites.get(orderId);
        if (!writePromise) {
          writePromise = wc
            .writeContract({
              address: token,
              abi: erc20Abi,
              functionName: 'transfer',
              args: [seller, amountRaw],
              chain: bsc,
              account: address,
            })
            .finally(() => {
              inflightWrites.delete(orderId);
            });
          inflightWrites.set(orderId, writePromise);
        }

        let hash: Hash;
        try {
          hash = await waitForTransferOrHash({
            writePromise,
            publicClient,
            token,
            from: address,
            to: seller,
            minRaw: amountRaw,
            startBlock: startBlock > 5n ? startBlock - 5n : 0n,
          });
        } catch (e) {
          const raw = errMessage(e, t);
          if (isUserReject(raw) && claimedThisSession) {
            try {
              await epAPI.releaseClaim({ order_id: orderId });
            } catch {
              /* best-effort */
            }
          }
          throw e instanceof Error ? e : new Error(raw);
        }

        setTxHash(hash);
        setIsMarkingPaid(true);
        try {
          await epAPI.markPaid({ order_id: orderId, pay_proof: hash });
        } finally {
          setIsMarkingPaid(false);
        }

        setPayConfig(null);
        setActiveOrderId(null);
        resolvePay(hash);
        return hash;
      } catch (e) {
        const msg = errMessage(e, t);
        setError(msg);
        rejectPay(e instanceof Error ? e : new Error(msg));
        throw e instanceof Error ? e : new Error(msg);
      } finally {
        inflightPays.delete(orderId);
        setIsPaying(false);
      }
    },
    [
      ensureWalletReady,
      isConnected,
      payConfig,
      prepareMut,
      usdtBalance,
      publicClient,
      address,
      walletClient,
      t,
    ],
  );

  const reset = useCallback(() => {
    setPayConfig(null);
    setTxHash(null);
    setError(null);
    setActiveOrderId(null);
    setIsPaying(false);
  }, []);

  return {
    payWithUsdt,
    reset,
    error,
    isBusy,
    activeOrderId,
    payConfig,
    txHash,
    usdtBalance,
  };
}
